import { FolderBreadcrumbData } from 'domains/core/components/folders/types';
import { Folder } from 'models/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { VoiceContentStateTypes } from 'pages/ContentVoiceBuilderPage/types';

import type { IBeeConfig } from '@beefree.io/sdk/dist/types/bee';

export type BeeConfig = {
    access_token: string;
    client_uid: string;
    mergeTags: MergeTag[];
    specialLinks?: SpecialLink[];
    contentDefaults: any;
    editorFonts: any;
    autosave: number | boolean;
    defaultForm?: any;
};

/** Email builder: base Bee token config plus optional Beefree SDK keys (saved rows, hooks, etc.). */
export type BeeEmailEditorConfig = BeeConfig &
    Partial<
        Pick<
            IBeeConfig,
            | 'saveRows'
            | 'hooks'
            | 'rowsConfiguration'
            | 'contentDialog'
            | 'loadingSpinnerDisableOnDialog'
            | 'loadingSpinnerDisableOnSave'
        >
    >;

export type MergeTag = {
    name: string;
    value: string;
    previewValue: string;
};

export type SpecialLink = {
    type: string;
    label: string;
    link: string;
};

export type SenderProfile = {
    id: number;
    name: string;
    from_address: string;
    reply_address: string;
};

export type DirectMailSenderProfile = {
    id: number;
    name: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    stateCode?: string;
    email?: string;
    phoneNumber?: string;
    jobTitle?: string;
    zipCode?: string;
    countryCode?: string;
};

export type VoiceCallerProfile = {
    id: number;
    name: string;
    number?: string;
    countryCode?: string;
    isVerified?: boolean;
};

export type SMSSenderProfile = {
    id: number;
    name: string;
    number?: string;
    countryCode?: string;
    isVerified?: boolean;
};

export enum ContentType {
    ALL = 'all',
    FOLDER = 'folder',
    EMAIL = 'email',
    PAGE = 'page',
    DIRECT_MAIL = 'direct_mail',
    SMS = 'sms',
    VOICE = 'voice',
    WHATSAPP = 'whatsapp',
}

export type ContentTag = {
    applicationsAccess: ApplicationAccessType | string;
    communicationType: CommunicationType;
};

export type DirectMail = ContentTag & {
    id: number;
    name: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    html: string | null;
    design: any; // TODO: use real type from unlayer
    htmlJson?: Record<string, string>;
    designJson?: Record<string, any>;
    type?: string;
    isArchived?: boolean;
    status?: ContentStatus;
};

export type VoiceContent = {
    id: number;
    name: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    type?: string;
    isArchived?: boolean;
};

export type Template = AssessmentData &
    ContentTag & {
        id: number;
        name: string;
        html: string;
        createdTimestamp: string;
        lastModifiedTimestamp: string;
        isLayout: boolean;
        beeJson?: string;
        preHeader?: string;
        senderProfileId?: number;
        subjectLine?: string;
        // This is only used by the type guard
        templateFields?: any;
        // This is neccessary as we are passing Template | LandingPage | Content as the type to the
        // ContentTable component. We have a new key 'type' on the content API and hence this needs to
        // be added as optional to both Template and LandingPage types.
        type?: string;
        isArchived?: boolean;
        status?: ContentStatus;
        // Multilingual fields
        htmlJson?: Record<string, string>;
        beeMlJson?: Record<string, any>;
        subjectLineJson?: Record<string, string>;
        preHeaderJson?: Record<string, string>;
    };

export type LandingPage = {
    beeJson?: string;
    createdTimestamp: string;
    deletedTimestamp?: string;
    id: number;
    lastModifiedTimestamp: string;
    name: string;
    parentFolderId?: number;
    type?: string;
    isArchived?: boolean;
};

export type SMS = AssessmentData &
    ContentTag & {
        content: string;
        createdTimestamp: string;
        defaultTemplateData: object;
        id: number;
        lastModifiedTimestamp: string;
        name: string;
        parentFolderId?: number;
        templateFields: string;
        type?: string;
        isArchived?: boolean;

        autoResponses?: SMSAutoResponse[];
        canDelete?: boolean;
        status?: ContentStatus;
        // Multilingual fields
        smsMlJson?: Record<string, any>;
        autoResponsesMl?: Record<string, any[]>;
    };

// ─── WhatsApp Template Type Enums ────────────────────────────────────────────

export enum TemplateHeaderFormat {
    TEXT = 'TEXT',
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    DOCUMENT = 'DOCUMENT',
    LOCATION = 'LOCATION',
}

export enum TemplateComponentButtonType {
    QUICK_REPLY = 'QUICK_REPLY',
    URL = 'URL',
    PHONE_NUMBER = 'PHONE_NUMBER',
    COPY_CODE = 'COPY_CODE',
    COPY_OFFER_CODE = 'COPY_OFFER_CODE',
    FLOW = 'FLOW',
}

export enum WhatsAppTemplateType {
    STANDARD = 'standard',
    CAROUSEL = 'carousel',
    LTO = 'lto',
}

// ─── Button Types ─────────────────────────────────────────────────────────────

export type WhatsAppQuickReplyButton = {
    type: TemplateComponentButtonType.QUICK_REPLY;
    text: string;
};

export type WhatsAppUrlButton = {
    type: TemplateComponentButtonType.URL;
    text: string;
    url: string;
};

export type WhatsAppPhoneButton = {
    type: TemplateComponentButtonType.PHONE_NUMBER;
    text: string;
    phone_number: string;
};

export type WhatsAppCopyCodeButton = {
    type: TemplateComponentButtonType.COPY_CODE;
    text?: string;
};

export type WhatsAppCopyOfferCodeButton = {
    type: TemplateComponentButtonType.COPY_OFFER_CODE;
    copy_code: string;
};

export type WhatsAppFlowButton = {
    type: TemplateComponentButtonType.FLOW;
    text: string;
    flow_id: string;
    flow_action?: string;
    navigate_screen?: string;
};

export type WhatsAppButton =
    | WhatsAppQuickReplyButton
    | WhatsAppUrlButton
    | WhatsAppPhoneButton
    | WhatsAppCopyCodeButton
    | WhatsAppCopyOfferCodeButton
    | WhatsAppFlowButton;

// ─── Location Data ────────────────────────────────────────────────────────────

export type WhatsAppLocationData = {
    latitude: string;
    longitude: string;
    name?: string;
    address?: string;
};

// ─── Component Types (discriminated union on `type`) ─────────────────────────

export type WhatsAppHeaderComponent = {
    type: 'HEADER';
    format: TemplateHeaderFormat;
    text?: string; // TEXT format
    url?: string; // IMAGE / VIDEO / DOCUMENT format
    location?: WhatsAppLocationData; // LOCATION format
};

export type WhatsAppBodyComponent = {
    type: 'BODY';
    text: string;
};

export type WhatsAppFooterComponent = {
    type: 'FOOTER';
    text: string;
};

export type WhatsAppButtonsComponent = {
    type: 'BUTTONS';
    buttons: WhatsAppButton[];
};

// Carousel card uses a flat representation for React state management.
// Serialised to Meta's nested components format on send.
export type WhatsAppCarouselCard = {
    header_format: TemplateHeaderFormat.IMAGE | TemplateHeaderFormat.VIDEO;
    header_url?: string;
    body_text: string;
    buttons: Array<WhatsAppQuickReplyButton | WhatsAppUrlButton>;
};

export type WhatsAppCarouselComponent = {
    type: 'CAROUSEL';
    cards: WhatsAppCarouselCard[];
};

export type WhatsAppLTOComponent = {
    type: 'LIMITED_TIME_OFFER';
    limited_time_offer: {
        text: string;
        has_expiration: boolean;
        expiration_time_ms?: number;
    };
};

// Main discriminated union — replaces the old flat WhatsAppComponent type
export type WhatsAppComponent =
    | WhatsAppHeaderComponent
    | WhatsAppBodyComponent
    | WhatsAppFooterComponent
    | WhatsAppButtonsComponent
    | WhatsAppCarouselComponent
    | WhatsAppLTOComponent;

// ─── Type Guards ──────────────────────────────────────────────────────────────

export const isHeaderComponent = (c: WhatsAppComponent): c is WhatsAppHeaderComponent => c.type === 'HEADER';
export const isBodyComponent = (c: WhatsAppComponent): c is WhatsAppBodyComponent => c.type === 'BODY';
export const isFooterComponent = (c: WhatsAppComponent): c is WhatsAppFooterComponent => c.type === 'FOOTER';
export const isButtonsComponent = (c: WhatsAppComponent): c is WhatsAppButtonsComponent => c.type === 'BUTTONS';
export const isCarouselComponent = (c: WhatsAppComponent): c is WhatsAppCarouselComponent => c.type === 'CAROUSEL';
export const isLTOComponent = (c: WhatsAppComponent): c is WhatsAppLTOComponent => c.type === 'LIMITED_TIME_OFFER';

// ─── WhatsApp Template ────────────────────────────────────────────────────────

export type WhatsApp = ContentTag & {
    id: number;
    name: string;
    language: string;
    components: WhatsAppComponent[];
    componentsJson?: Record<string, WhatsAppComponent[]>;
    defaultTemplateData: object;
    templateFields: Record<string, string>;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    deletedAt?: string | null;
    parentFolderId?: number;
    type?: string;
    isArchived?: boolean;
    canDelete?: boolean;
    status?: ContentStatus;
    whatsappAccountId?: string;
    whatsappNamespace?: string;
    whatsappTemplateId?: string;
    // For compatibility with editor — extracted from BODY component
    content?: string;
};

export type VoiceCallContent = ContentTag & {
    id: number;
    name: string;
    voiceJson: VoiceContentStateTypes['voiceJson'];
    defaultTemplateData: string;
    templateFields: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    type?: string;
    isArchived?: boolean;
    status?: string;
};

export type AssessmentData = {
    assessmentId?: string;
    assessmentName?: string;
    assessmentConsentRequired?: boolean;
};

export type SMSAutoResponse = {
    keywords: string[];
    text: string;
    validFor?: {
        interval: number;
        units: 'days' | 'hours';
    };
};

export type CreateLandingPageInput = Pick<LandingPage, 'beeJson'> &
    Partial<Pick<LandingPage, 'name' | 'parentFolderId'>>;
export type CreateTemplateInput = Pick<Template, 'beeJson'> &
    ContentTag & {
        parentFolderId?: number;
        templateName?: string;
        beeMlJson?: Record<string, any>;
        subjectLineJson?: Record<string, string>;
        preHeaderJson?: Record<string, string>;
    };
export type CreateVoiceContentInput = {
    name?: string;
    voiceJson?: VoiceContentStateTypes['voiceJson'];
};
export type UpdateLandingPageInput = Pick<LandingPage, 'beeJson'> &
    Partial<Pick<LandingPage, 'name' | 'parentFolderId'>> & { id: string };
export type UpdateTemplateInput = Pick<Template, 'beeJson'> &
    ContentTag &
    AssessmentData & {
        id: string;
        parentFolderId?: number;
        preHeader?: string;
        senderProfileId?: number;
        subjectLine?: string;
        templateName?: string;
        beeMlJson?: Record<string, any>;
        subjectLineJson?: Record<string, string>;
        preHeaderJson?: Record<string, string>;
    };
export type UpdateVoiceContentInput = {
    id: string;
    name?: string;
    type?: ContentType;
    parentFolderId?: number;
    voiceJson?: VoiceContentStateTypes['voiceJson'];
};

export type UpdateContentStatusInput = {
    status: string;
    channel: string;
};

export function isTemplate(obj: any): obj is Template {
    return obj && typeof obj === 'object' && 'beeJson' in obj && 'templateFields' in obj;
}
export function isLandingPage(obj: any): obj is LandingPage {
    return obj && typeof obj === 'object' && 'beeJson' in obj && !('templateFields' in obj);
}

export enum ContentTypeEnumForBackend {
    'Default' = '',
    'Direct Mail' = 'direct_mail',
    'Email' = 'email',
    'Folder' = 'folder',
    'Landing Page' = 'landing_page',
    'SMS' = 'sms',
    'voice' = 'voice',
    'Whatsapp' = 'whatsapp',
}

export enum ContentTypeForComparison {
    DEFAULT = 'Default',
    DIRECT_MAIL = 'Direct Mail',
    EMAIL = 'Email',
    FOLDER = 'Folder',
    LANDING_PAGE = 'Landing Page',
    SMS = 'SMS',
    VOICE = 'voice',
    WHATSAPP = 'Whatsapp',
}

export type ContentModalContentType =
    | DirectMail
    | Template
    | LandingPage
    | SMS
    | WhatsApp
    | Folder
    | FolderBreadcrumbData
    | VoiceContent;

export const RootContentFolder = {
    name: 'All Content',
    id: -1,
    type: ContentTypeForComparison.FOLDER,
};

export enum CommunicationType {
    COMMERCIAL = 'commercial',
    TRANSACTIONAL = 'transactional',
}

export enum ApplicationAccessType {
    OUTREACH = 'outreach',
    INCARE = 'in_care',
    INJOURNEY = 'in_journey',
    ACCESS = 'access',
}
